/**
 * Page-editor adapter for the headless Review moment.
 *
 * Chat owns the review chrome (`ReviewMomentFooter`). This bridge only
 * forwards enabled state, the source-message pin, and undo / redo / diff
 * callbacks into the shared slot. Staging is a same-origin iframe, so the
 * payload must stay serializable flags + `onAction` (no editor-created React
 * tree).
 *
 * Renders nothing.
 */
import { useCallback, useEffect, useRef } from 'react';

import {
	insertReviewMomentFooter,
	removeReviewMomentFooter,
} from '@atlassian/conversation-assistant/controllers/message-footer-slot/insertReviewMomentFooter';
import { getFooterSlotGeneration } from '@atlassian/conversation-assistant/controllers/message-footer-slot/messageFooterSlotStore';

export const ReviewChatBridge = ({
	available,
	canRedo,
	canUndo,
	isDisplayingDiff,
	onRedo,
	onToggleDiff,
	onUndo,
	sourceMessageId,
}: {
	available: boolean;
	canRedo: boolean;
	canUndo: boolean;
	isDisplayingDiff: boolean;
	onRedo: () => void;
	onToggleDiff: () => void;
	onUndo: () => void;
	/** Assistant message that applied this review. Pins View changes to that bubble. */
	sourceMessageId?: string;
}): null => {
	// Capture at mount so a later create / conversation change (which bumps
	// the window.top store generation) cannot re-insert this editor's Undo
	// into chat, and so unmount cannot remove a newer document's footer.
	const generationRef = useRef(getFooterSlotGeneration());
	// Keep the first non-empty pin so a later session start cannot move this
	// still-mounted review onto a different (or the latest) bubble.
	const sourceMessageIdRef = useRef(sourceMessageId);
	if (sourceMessageId) {
		sourceMessageIdRef.current = sourceMessageId;
	}

	const onUndoRef = useRef(onUndo);
	onUndoRef.current = onUndo;
	const onRedoRef = useRef(onRedo);
	onRedoRef.current = onRedo;
	const onToggleDiffRef = useRef(onToggleDiff);
	onToggleDiffRef.current = onToggleDiff;

	const onRemoteAction = useCallback((actionId: string) => {
		if (actionId === 'undo') {
			onUndoRef.current();
			return;
		}
		if (actionId === 'redo') {
			onRedoRef.current();
			return;
		}
		if (actionId === 'toggle-diff') {
			onToggleDiffRef.current();
		}
	}, []);

	useEffect(() => {
		if (!available && !isDisplayingDiff) {
			removeReviewMomentFooter(generationRef.current);
			return;
		}

		insertReviewMomentFooter(
			{
				canRedo,
				canUndo,
				isDisplayingDiff,
			},
			onRemoteAction,
			generationRef.current,
			sourceMessageIdRef.current,
		);
	}, [available, canRedo, canUndo, isDisplayingDiff, onRemoteAction, sourceMessageId]);

	useEffect(() => {
		const generation = generationRef.current;
		return () => {
			removeReviewMomentFooter(generation);
		};
	}, []);

	return null;
};
